<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Referrals..."
    button-location="top"
    @submit="searchReferrals()"
  >
    <template #title>
      Referral Search
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Search Type</label>
        <div class="col-sm-5">
          <dropdown-list
            v-model="searchCondition.type"
            :options="queryTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Search Value</label>
        <div class="col-sm-5">
          <phone-number-input
            v-if="isSearchByPhoneNumber"
            v-model="searchCondition.query"
            :page-id="pageId"
          />
          <sanitized-input
            v-if="!isSearchByPhoneNumber"
            id="query"
            v-model="searchCondition.query"
            placeholder="Please input search value"
          />
        </div>
      </div>

      <div class="form-submit-button-group">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="isBtnDisabled"
        >
          Search
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>
      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="searchReferrals"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="referrals"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed } from 'vue';
import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { ListType } from '@/models/app';
import { TableColumnType } from '@/models/table';

import { marketingService } from '@/services/marketing';
import { ReferralsQuery } from '@/gql/graphql';

type PaginatedReferralTypeResponse = Extract<ReferralsQuery['referrals'], {}>;
type Referral = Extract<PaginatedReferralTypeResponse['rows'], {}>[number] & {
  referrerName?: string;
  referrerPhone?: string;
  refereeName?: string;
  refereePhone?: string;
};

const pageId = 'search-referrals';
const paginationLimit = 30;
const queryTypeList: ListType[] = [
  { id: 1, key: 'phoneNumber', label: 'Phone Number' },
  { id: 2, key: 'referralCode', label: 'Referral Code' },
];

const tableColumns: TableColumnType[] = [
  { label: 'Created At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Referral Code', field: 'referralCode', width: 120, isCenterAlign: true },
  { label: 'Referrer', field: 'referrerName', width: 150, isCenterAlign: true },
  { label: 'Referrer Phone', field: 'referrerPhone', width: 140, isCenterAlign: true },
  { label: 'Referee', field: 'refereeName', width: 150, isCenterAlign: true },
  { label: 'Referee Phone', field: 'refereePhone', width: 140, isCenterAlign: true },
  { label: 'Promo Id', field: 'promoId', width: 200, isCenterAlign: true },
];

const { showErrorPopupDialog } = useSwalDialog();

const searchCondition: Ref<any> = ref({
  type: 'phoneNumber',
  query: '',
});
const referrals: Ref<Referral[]> = ref([]);
const status = reactive({
  isLoading: false,
  totalPages: 1,
  currentPage: 1,
});
const isSearchByPhoneNumber = computed(() => searchCondition.value.type === 'phoneNumber');
const isBtnDisabled = computed(() => {
  if (status.isLoading) {
    return true;
  }

  if (isSearchByPhoneNumber.value && searchCondition.value.query && !util.isPhoneNumberValid(searchCondition.value.query)) {
    return true;
  }

  return false;
});

const searchReferrals = async (currentPage: number = 1) => {
  status.isLoading = true;
  status.currentPage = currentPage;

  const offset = (currentPage - 1) * paginationLimit;
  const queryInput: any = {
    pagination: {
      limit: paginationLimit,
      offset,
    },
  };

  if (searchCondition.value.query) {
    queryInput[searchCondition.value.type] = searchCondition.value.query;
  }

  try {
    const response = await marketingService.fetchReferrals(queryInput);

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    referrals.value = response.rows.map((row: Referral) => ({
      ...row,
      referrerName: `${row.referrer?.firstName} ${row.referrer?.lastName}`,
      referrerPhone: row.referrer?.phoneNumber ?? row.referrer?.homeNumber ?? row.referrer?.phoneNumber2 ?? '',
      refereeName: `${row.referee?.firstName} ${row.referee?.lastName}`,
      refereePhone: row.referee?.phoneNumber ?? row.referee?.homeNumber ?? row.referee?.phoneNumber2 ?? '',
    }));
  } catch (error) {
    referrals.value = [];
    status.totalPages = 1;
    status.currentPage = 1;

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const resetForm = () => {
  status.totalPages = 1;
  status.currentPage = 1;

  referrals.value = [];

  searchCondition.value = {
    type: queryTypeList[0].key,
    query: '',
  };
};

watch(
  () => searchCondition.value.type,
  () => {
    searchCondition.value.query = '';
  },
);

util.moveToTop();
</script>
